import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DashboardLayoutAccountSidebar } from '../dashboard-layout/DashboardLayout';
import { LeftPaneMenu } from '../navigation';
import { useAuth } from '../auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import useSettingsSections from '@/hooks/useSettingsSections';
import { useSettingsLeaveGuard } from '@/hooks/useSettingsLeaveGuard';
import { useSettingsRouteGuard } from '@/hooks/useSettingsRouteGuard';
import { getRememberedChatPath } from '@/utils/lastChatPath';
import { installInputModalityTracker } from '@/utils/inputModality';
import PageLoader from '@/ui-library/PageLoader';
import AnswerStyleSettingsSection from './AnswerStyleSettingsSection';
import { LanguageSettingsSection, NavigationSettingsSection } from './GeneralSettingsSections';
import {
  ProfileSettingsSection,
  NameSettingsSection,
  DeleteAccountSection,
} from './AccountSettingsSections';
import {
  SourcesSettingsSection,
  AiDataSettingsSection,
  LegalPrivacySettingsSection,
} from './PrivacySettingsSections';
import BallotSettingsSection from './BallotSettingsSection';
import NotificationSettingsSection from './NotificationSettingsSection';
import MembershipSettingsSection from './MembershipSettingsSection';
import SettingsDiscardDialog from './SettingsDiscardDialog';
import SettingsLoadingState from './SettingsLoadingState';
import type { SettingsEditState } from './settingsTypes';

/** Full-page fallback for direct links and phone browsers. Desktop menus use SettingsDialog. */
export function SettingsContainer() {
  const [leftPaneOpen, setLeftPaneOpen] = useState(false);
  const [triggerAccountMenu, setTriggerAccountMenu] = useState(false);
  const [answerEdit, setAnswerEdit] = useState<SettingsEditState | null>(null);
  const [operationBusy, setOperationBusy] = useState(false);
  const { user, isInitializing, signOut } = useAuth();
  const settings = useSettingsSections();
  const { loadUserSettings } = settings;
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [privacyVisit, setPrivacyVisit] = useState({ key: location.key, handled: false });
  if (privacyVisit.key !== location.key) {
    setPrivacyVisit({ key: location.key, handled: false });
  }
  const focusPrivacy = searchParams.get('section') === 'privacy' && !privacyVisit.handled;
  const markPrivacyArrivalHandled = useCallback(
    () => setPrivacyVisit({ key: location.key, handled: true }),
    [location.key]
  );
  const requestedSettings = useRef(false);

  useEffect(() => installInputModalityTracker(), []);
  useEffect(() => {
    if (!isInitializing && user && !requestedSettings.current) {
      requestedSettings.current = true;
      void loadUserSettings();
    }
  }, [isInitializing, user, loadUserSettings]);

  const groups = settings.groups;
  const dirty =
    !!answerEdit?.dirty ||
    (!!user && (groups.name.dirty || groups.address.dirty || groups.phone.dirty));
  const busy =
    operationBusy ||
    !!answerEdit?.saving ||
    Object.values(groups).some((group) => group.state === 'saving');
  const discard = useCallback(() => {
    if (groups.name.dirty) settings.cancelName();
    if (groups.address.dirty) settings.cancelAddress();
    if (groups.phone.dirty) settings.cancelPhone();
    if (answerEdit?.dirty) answerEdit.discard();
  }, [groups, settings, answerEdit]);
  const leave = useSettingsLeaveGuard({ dirty, busy, onDiscard: discard });
  const routeGuard = useSettingsRouteGuard({ dirty, busy, requestLeave: leave.requestLeave });

  const goBack = () =>
    leave.requestLeave(() => {
      const stored = getRememberedChatPath();
      navigate(user ? (stored?.startsWith('/chat') ? stored : '/chat') : '/chat?guest=true');
    });
  const guardedSignOut = () =>
    leave.requestLeave(() => {
      void signOut();
    });
  const onOpenBallot = () =>
    leave.requestLeave(() => {
      analytics.track('Sample Ballot Viewed', { source: 'Left Menu - Settings Page' });
      navigate('/chat?state=ballot-open');
    });

  if (isInitializing) return <PageLoader />;

  return (
    <>
      <DashboardLayoutAccountSidebar
        showWhiteStrip
        onMenuClick={() => leave.requestLeave(() => setLeftPaneOpen((value) => !value))}
        onAccountIconClick={
          user
            ? () =>
                leave.requestLeave(() => {
                  setLeftPaneOpen(true);
                  setTriggerAccountMenu(true);
                })
            : undefined
        }
        guestMode={!user}
        hideAccountButton={!user}
        menuOpen={leftPaneOpen}
        pageScroll
      >
        <Box
          sx={{
            width: { xs: '100%', sm: '85vw', md: '70vw', lg: '580px' },
            maxWidth: '100%',
            mx: 'auto',
            mb: 4,
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: 4,
          }}
        >
          <Button
            onClick={goBack}
            variant="text"
            disableRipple
            startIcon={<ChevronLeftIcon sx={{ fontSize: '24px !important' }} />}
            disabled={busy}
            sx={{
              color: 'inherit',
              padding: 0,
              minWidth: 0,
              textTransform: 'none',
              mb: 3,
              fontSize: '1.5rem',
              fontWeight: 700,
              '&:hover': { background: 'none', opacity: 0.7 },
            }}
          >
            {t('title')}
          </Button>
          {busy && (
            <Typography role="status" sx={{ mb: 2, fontSize: '12px' }}>
              {t('savingBeforeLeaving')}
            </Typography>
          )}
          <ProfileSettingsSection onSignOut={guardedSignOut} />
          {user && (!settings.settingsLoaded || settings.settingsLoadError) ? (
            <>
              <SettingsLoadingState
                error={settings.settingsLoadError}
                onRetry={() => void settings.loadUserSettings()}
              />
              <LegalPrivacySettingsSection
                settings={settings}
                focusPrivacy={focusPrivacy}
                onPrivacyArrival={markPrivacyArrivalHandled}
              />
              <AiDataSettingsSection />
            </>
          ) : (
            <>
              {user && (
                <>
                  <NameSettingsSection settings={settings} />
                  <LanguageSettingsSection settings={settings} />
                </>
              )}
              <AnswerStyleSettingsSection onEditStateChange={setAnswerEdit} />
              <NavigationSettingsSection settings={settings} />
              {user && (
                <>
                  <BallotSettingsSection settings={settings} onBusyChange={setOperationBusy} />
                  <NotificationSettingsSection settings={settings} />
                </>
              )}
              <MembershipSettingsSection />
              <SourcesSettingsSection />
              <AiDataSettingsSection />
              <LegalPrivacySettingsSection
                settings={settings}
                focusPrivacy={focusPrivacy}
                onPrivacyArrival={markPrivacyArrivalHandled}
              />
              {user && (
                <DeleteAccountSection
                  onBusyChange={setOperationBusy}
                  onRequestDelete={leave.requestLeave}
                />
              )}
            </>
          )}
        </Box>
      </DashboardLayoutAccountSidebar>
      <LeftPaneMenu
        isOpen={leftPaneOpen}
        onClose={() => setLeftPaneOpen(false)}
        guestMode={!user}
        onOpenBallot={onOpenBallot}
        shouldOpenAccountMenu={triggerAccountMenu}
        onAccountMenuOpened={() => setTriggerAccountMenu(false)}
      />
      <SettingsDiscardDialog
        open={leave.confirmationOpen}
        busy={busy}
        onKeep={() => {
          leave.keepEditing();
          routeGuard.resetBlockedNavigation();
        }}
        onDiscard={leave.discardAndLeave}
      />
    </>
  );
}
